import type { Component } from "solid-js";
import * as Router from "@solidjs/router";

interface Crumb {
  label: string;
  href?: string;
}

const LABELS: Record<string, string> = {
  accounts: "Accounts",
  analytics: "Analytics",
  billing: "Billing",
  bundles: "Bundles",
  "cache-rules": "Cache Rules",
  clients: "Clients",
  credentials: "Credentials",
  deployments: "Deployments",
  diagnostics: "Diagnostics",
  gateways: "Gateways",
  home: "Home",
  instances: "Instances",
  observability: "Observability",
  roles: "Roles",
  sessions: "Sessions",
  settings: "Settings",
  tunnels: "Tunnels",
  users: "Users",
  "cloud-credentials": "Cloud Credentials",
  "component-versions": "Component Versions",
  "role-templates": "Role Templates",
  "update-campaigns": "Update Campaigns",
};

function titleize(segment: string): string {
  return segment
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function singularFor(parent: string): string {
  switch (parent) {
    case "clients":
      return "Client";
    case "deployments":
      return "Deployment";
    case "credentials":
    case "cloud-credentials":
      return "Credential";
    case "gateways":
      return "Gateway";
    case "bundles":
      return "Bundle";
    case "roles":
    case "role-templates":
      return "Role";
    default:
      return "Details";
  }
}

function inferPathAndParams(): { pathname: string; params: Record<string, string> } {
  const pathname = typeof window !== "undefined" ? window.location.pathname : "/";
  try {
    const useParams = (Router as unknown as { useParams?: () => Record<string, string> }).useParams;
    return { pathname, params: typeof useParams === "function" ? useParams() : {} };
  } catch {
    return { pathname, params: {} };
  }
}

function buildFallbackCrumbs(params: Record<string, string>): Crumb[] {
  if (params.clientId && params.deploymentId) {
    return [
      { label: "Clients", href: "/clients" },
      { label: "Client", href: `/clients/${params.clientId}` },
      { label: "Deployments", href: `/clients/${params.clientId}/deployments` },
    ];
  }

  if (params.clientId && params.credentialId) {
    return [
      { label: "Clients", href: "/clients" },
      { label: "Cloud Credentials", href: `/clients/${params.clientId}/cloud-credentials` },
    ];
  }

  if (params.id) {
    return [
      { label: "Bundles", href: "/bundles" },
    ];
  }

  return [];
}

function buildCrumbs(pathname: string, params: Record<string, string>): Crumb[] {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) {
    return buildFallbackCrumbs(params);
  }

  const crumbs: Crumb[] = [];
  let href = "";
  for (let index = 0; index < segments.length; index += 1) {
    const segment = segments[index]!;
    href += `/${segment}`;
    const prev = segments[index - 1];
    const isDynamic = /^([a-z]+-)?[a-f0-9-]{4,}$/i.test(segment) || Object.values(params).includes(segment);

    if (isDynamic) {
      crumbs.push({ label: singularFor(prev ?? ""), href });
      continue;
    }

    crumbs.push({ label: LABELS[segment] ?? titleize(segment), href });
  }

  return crumbs.length > 0 ? crumbs : buildFallbackCrumbs(params);
}

export const Breadcrumb: Component = () => {
  let Link: Component<Record<string, unknown>> | undefined;
  try {
    Link = (Router as unknown as { A?: Component<Record<string, unknown>> }).A;
  } catch {
    Link = undefined;
  }
  const { pathname, params } = inferPathAndParams();
  const crumbs = buildCrumbs(pathname, params);

  if (crumbs.length === 0) return null;

  return (
    <nav aria-label="Breadcrumb" class="breadcrumb">
      <ol
        style={{
          display: "flex",
          gap: "8px",
          "list-style": "none",
          margin: "0 0 12px 0",
          padding: "0",
          "flex-wrap": "wrap",
          color: "var(--text-secondary)",
          "font-size": "14px",
        }}
      >
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;
          const content = crumb.href && Link && !isLast
            ? <Link href={crumb.href}>{crumb.label}</Link>
            : <span aria-current={isLast ? "page" : undefined}>{crumb.label}</span>;
          return (
            <li style={{ display: "inline-flex", gap: "8px", "align-items": "center" }}>
              {content}
              {!isLast ? <span aria-hidden="true">/</span> : null}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
